import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment, 
  IconButton, 
  Button, 
  Card, 
  CardContent,
  Skeleton, 
  Alert, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Tooltip,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import SortIcon from '@mui/icons-material/Sort';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StraightenIcon from '@mui/icons-material/Straighten';
import PrintIcon from '@mui/icons-material/Print';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MapIcon from '@mui/icons-material/Map';
import ConfirmationDialog from './ConfirmationDialog';
import PrintableReport from './PrintableReport';
import { SavedLocation, TimeWindow } from '../types';
import { format, parseISO } from 'date-fns';
import { exportToFile, importFromFile } from '../utils/fileStorage';
import { calculateRoutes, calculateRouteFromCurrentLocation, LocationRouteInfo } from '../services/mapboxService';
import { createSavedLocation } from '../services/savedLocations';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

interface SavedLocationsPanelProps {
  savedLocations: SavedLocation[];
  onSelectLocation: (location: SavedLocation) => void;
  onDeleteLocation: (id: string) => void;
  selectedLocationId: string | null;
  loading?: boolean;
  onImportLocations?: (locations: SavedLocation[]) => void;
  onReorderLocations?: (reorderedLocations: SavedLocation[]) => void;
  onToggleRoute?: (visible: boolean) => void;
  onUpdateRouteGeometry?: (geometry: any) => void;
  routeVisible: boolean;
}

// Helper function to create Google Maps navigation link
const getGoogleMapsLink = (lat: number, lon: number): string => {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
};

// Helper function to get cardinal direction from angle
const getCardinalDirection = (angle: number): string => {
  const normalizedAngle = ((angle % 360) + 360) % 360;
  if (normalizedAngle >= 337.5 || normalizedAngle < 22.5) return 'N';
  if (normalizedAngle >= 22.5 && normalizedAngle < 67.5) return 'NE';
  if (normalizedAngle >= 67.5 && normalizedAngle < 112.5) return 'E';
  if (normalizedAngle >= 112.5 && normalizedAngle < 157.5) return 'SE';
  if (normalizedAngle >= 157.5 && normalizedAngle < 202.5) return 'S';
  if (normalizedAngle >= 202.5 && normalizedAngle < 247.5) return 'SW';
  if (normalizedAngle >= 247.5 && normalizedAngle < 292.5) return 'W';
  return 'NW';
};

const getOppositeCardinalDirection = (direction: string): string => {
  const opposites: { [key: string]: string } = {
    'N': 'S', 'NE': 'SW', 'E': 'W', 'SE': 'NW',
    'S': 'N', 'SW': 'NE', 'W': 'E', 'NW': 'SE'
  };
  return opposites[direction] || direction;
};

const formatCardinalDirectionPair = (direction: string): string => {
  return `${direction}-${getOppositeCardinalDirection(direction)}`;
};

const createNewLocation = (): SavedLocation => ({
  id: "-1",
  name: "New Location",
  latitude: 0,
  longitude: 0,
  row_direction: 0,
  plant_height: 0,
  row_distance: 100,
  min_angle_from_row: 0,
  min_distance_from_row: 0,
  selected_date: new Date().toISOString().split('T')[0],
  optimal_time_windows: [],
  created_at: new Date().toISOString(),
  order: 0
});

const SavedLocationsPanel: React.FC<SavedLocationsPanelProps> = ({
  savedLocations,
  onSelectLocation,
  onDeleteLocation,
  selectedLocationId,
  loading = false,
  onImportLocations,
  onReorderLocations,
  onToggleRoute,
  onUpdateRouteGeometry,
  routeVisible
}) => {
  // State for menu anchor element (for popup menus)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [locationToDelete, setLocationToDelete] = useState<string | null>(null);
  const [confirmDeleteAllOpen, setConfirmDeleteAllOpen] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('order');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [printReportOpen, setPrintReportOpen] = useState<boolean>(false);
  const [calculatingRoute, setCalculatingRoute] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [routeData, setRouteData] = useState<{
    [key: string]: LocationRouteInfo | null
  }>({});
  const [routeError, setRouteError] = useState<string | null>(null);
  
  // Track previous location count to detect new locations
  const prevLocationCountRef = useRef(savedLocations.length);
  
  // Calculate route between saved locations using Mapbox Directions API
  const handleCalculateRoute = useCallback(async (forceCalculate = false) => {
    // If route data exists and we're not forcing calculation, toggle off
    if (Object.keys(routeData).length > 0 && !forceCalculate) {
      console.log('Route data exists and not forcing, toggling off');
      setRouteData({});
      onToggleRoute?.(false);
      onUpdateRouteGeometry?.(null);
      return;
    }

    // Skip if already calculating or no locations
    if (calculatingRoute || savedLocations.length === 0) {
      return;
    }

    console.log('Starting route calculation for', savedLocations.length, 'locations');
    
    setCalculatingRoute(true);
    setRouteError(null);
    
    try {
      // Initialize combined geometry for all routes including from current location
      const combinedRoutes = {
        betweenLocations: null as any,
        fromCurrentLocation: null as any
      };
      
      // Sort locations by order property for consistent routing
      const sortedLocations = [...savedLocations].sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });
      
      // Get current browser location for route calculation
      let userLocation: {latitude: number, longitude: number} | null = null;
      
      if (sortedLocations.length > 0) {
        try {
          // Get current position from browser
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            if (!navigator.geolocation) {
              reject(new Error('Geolocation is not supported by your browser'));
              return;
            }
            
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0
            });
          });
          
          userLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
        } catch (error) {
          console.error('Error getting current location:', error);
        }
      }
      
      // Calculate route from current browser location to first location if we have user location
      if (userLocation && sortedLocations.length > 0) {
        const firstLocation = sortedLocations[0];
        
        // Calculate route from current location to first saved location
        const { routeInfo: currentLocationRouteInfo, routeGeometry: fromCurrentLocationGeometry } = 
          await calculateRouteFromCurrentLocation(
            userLocation, 
            {
              latitude: firstLocation.latitude,
              longitude: firstLocation.longitude
            }
          );
        
        // Store route geometry from current location to first location
        if (fromCurrentLocationGeometry) {
          combinedRoutes.fromCurrentLocation = fromCurrentLocationGeometry;
        }
        
        // If we got the current location route info, save it
        if (currentLocationRouteInfo) {
          // Store the route info with a special key for the first location
          const combinedRouteData: {[key: string]: LocationRouteInfo} = {};
          combinedRouteData[`current_to_${firstLocation.id}`] = currentLocationRouteInfo;
          setRouteData(prev => ({...prev, ...combinedRouteData}));
        }
      }
      
      // Use the Mapbox Directions API to calculate routes between saved locations
      const result = await calculateRoutes(sortedLocations);
      
      // Store route geometry between locations
      if (result.routeGeometry) {
        combinedRoutes.betweenLocations = result.routeGeometry;
      }
      
      // Update route data with the routes between locations
      setRouteData(prev => ({...prev, ...result.routes}));
      
      // Update route geometry and visibility
      onUpdateRouteGeometry?.(combinedRoutes);
      onToggleRoute?.(true);
    } catch (error) {
      console.error('Error calculating route:', error);
      setRouteError('Failed to calculate route. Using straight-line distances instead.');
      
      // Fall back to the previous straight-line calculation
      const straightLineResult = savedLocations.reduce((acc, location, index) => {
        if (index > 0) {
          const prevLocation = savedLocations[index - 1];
          // Simple distance calculation
          const dx = (location.longitude - prevLocation.longitude) * 111.32 * Math.cos(location.latitude * (Math.PI / 180));
          const dy = (location.latitude - prevLocation.latitude) * 110.574;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Estimate time (assuming 60 km/h average speed)
          const timeInHours = distance / 60;
          const timeInMinutes = Math.round(timeInHours * 60);
          
          const locationId = location.id.toString();
          acc[locationId] = {
            distance: `${distance.toFixed(1)} km`,
            time: timeInMinutes < 60 
              ? `${timeInMinutes} min` 
              : `${Math.floor(timeInMinutes / 60)}h ${timeInMinutes % 60}min`,
            durationSeconds: timeInMinutes * 60,
            distanceMeters: distance * 1000
          };
        }
        return acc;
      }, {} as {[locationId: string]: LocationRouteInfo});
      
      setRouteData(straightLineResult);
      
      // Generate simple LineString for straight-line path
      const lineStringCoordinates = savedLocations.map(loc => [loc.longitude, loc.latitude]);
      
      // Create fallback route geometry
      const fallbackGeometry = {
        betweenLocations: {
          type: 'LineString',
          coordinates: lineStringCoordinates
        },
        fromCurrentLocation: null
      };
      
      onUpdateRouteGeometry?.(fallbackGeometry);
      onToggleRoute?.(true);
    } finally {
      setCalculatingRoute(false);
    }
  }, [savedLocations, routeData, onToggleRoute, onUpdateRouteGeometry, calculatingRoute]);
  
  // Track route visibility changes from parent
  useEffect(() => {
    // If route visibility is turned on, force route calculation
    if (routeVisible) {
      console.log('Route became visible, forcing calculation');
      handleCalculateRoute(true);
    }
  }, [routeVisible, handleCalculateRoute]);
  
  // Simplified route calculation trigger - only recalculate when:
  // 1. savedLocations array reference changes
  // 2. savedLocations.length changes (array size changes)
  // 3. Not in loading state
  // 4. Route data is empty
  const savedLocationsLength = savedLocations.length;
  
  useEffect(() => {
    // Only calculate routes when locations change, not already calculated,
    // and not in loading state
    if (
      savedLocationsLength >= 1 && 
      !loading && 
      Object.keys(routeData).length === 0
    ) {
      console.log('Locations changed, calculating routes');
      handleCalculateRoute(true);
    }
  }, [savedLocationsLength, loading, routeData, handleCalculateRoute]);
  
  // Handle sort menu
  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchorEl(event.currentTarget);
  };
  
  const handleSortClose = () => {
    setSortAnchorEl(null);
  };
  
  const handleSortChange = (field: 'name' | 'date' | 'direction' | 'order') => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setSortBy(field);
      setSortDirection(field === 'date' ? 'desc' : field === 'order' ? 'asc' : 'asc');
    }
    handleSortClose();
  };
  
  // Handle delete
  const handleDeleteClick = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setLocationToDelete(id);
    setConfirmDeleteOpen(true);
  };
  
  const handleConfirmDelete = () => {
    if (locationToDelete !== null) {
      onDeleteLocation(locationToDelete);
    }
    setConfirmDeleteOpen(false);
    setLocationToDelete(null);
  };
  
  // Handle delete all
  const handleDeleteAllClick = () => {
    setConfirmDeleteAllOpen(true);
  };
  
  const handleConfirmDeleteAll = () => {
    // Delete all locations
    savedLocations.forEach(location => {
      onDeleteLocation(location.id.toString());
    });
    setConfirmDeleteAllOpen(false);
  };
  
  // Handle export/import
  const handleExport = () => {
    try {
      exportToFile(savedLocations);
    } catch (error) {
      console.error('Error exporting locations:', error);
    }
  };
  
  const handleImport = async () => {
    try {
      const importedLocations = await importFromFile();
      
      // Generate unique IDs for imported locations
      const processedLocations = importedLocations.map(location => {
        // Remove any existing ID and ensure required fields
        const { id, ...locationData } = location;
        return {
          ...locationData,
          time: locationData.time || '12:00',  // Ensure time is present
          selected_date: locationData.selected_date || new Date().toISOString().split('T')[0]  // Ensure date is present
        };
      });
      
      // Create locations through service calls
      const createdLocations: SavedLocation[] = [];
      for (const location of processedLocations) {
        try {
          const createdLocation = await createSavedLocation(location);
          createdLocations.push(createdLocation);
        } catch (error) {
          console.error('Error importing location:', error);
        }
      }
      
      // Notify parent component about the imported locations
      if (onImportLocations) {
        onImportLocations(createdLocations);
      }
    } catch (error) {
      console.error('Error importing locations:', error);
    }
  };
  
  // Filter and sort locations
  const filteredAndSortedLocations = useMemo(() => {
    // Filter by search term
    let filtered = savedLocations;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = savedLocations.filter(location => 
        location.name.toLowerCase().includes(term)
      );
    }
    
    // Sort by selected field
    return [...filtered].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'order') {
        // Default to order property or index in array if order not defined
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        comparison = orderA - orderB;
      } else if (sortBy === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortBy === 'date') {
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortBy === 'direction') {
        comparison = a.row_direction - b.row_direction;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [savedLocations, sortBy, sortDirection, searchTerm]);
  
  // Format time windows for display
  const formatTimeWindows = (windows: TimeWindow[] | undefined): React.ReactElement[] => {
    if (!windows || windows.length === 0) {
      return [<Typography key="no-windows" variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
        No optimal time windows calculated
      </Typography>];
    }

    return windows.map((window, index) => (
      <Typography key={index} variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
        {window.start} - {window.end}
      </Typography>
    ));
  };

  // Handle drag and drop for reordering
  const handleDragEnd = (result: DropResult) => {
    console.log('DragEnd result:', result);
    
    if (!result.destination) {
      console.log('No destination provided');
      return;
    }
    
    if (!onReorderLocations) {
      console.log('No onReorderLocations callback provided');
      return;
    }

    // Don't reorder if dropping at the same position
    if (result.destination.index === result.source.index) {
      return;
    }

    console.log('Source index:', result.source.index);
    console.log('Destination index:', result.destination.index);

    // Make sure we're sorting by order for drag and drop to work properly
    setSortBy('order');
    setSortDirection('asc');

    const reorderedLocations = reorderLocations(
      filteredAndSortedLocations,
      result.source.index,
      result.destination.index
    );

    console.log('Reordered locations:', reorderedLocations);
    
    // Call the callback with reordered locations
    onReorderLocations(reorderedLocations);
    
    // Clear existing route data to force recalculation
    setRouteData({});
    
    // Recalculate routes after reordering
    setTimeout(() => {
      console.log('Recalculating routes after reordering');
      handleCalculateRoute(true);
    }, 300); // Small delay to ensure state is updated
  };

  // Helper function to reorder the list
  const reorderLocations = (list: SavedLocation[], startIndex: number, endIndex: number): SavedLocation[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 0.75, 
        pr: 0.5,
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 0,
        border: 'none'
      }}
    >
      <Box sx={{ px: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 1 }}>
          Saved Locations
        </Typography>
        
        {/* New Location button */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="small"
          disabled={loading || selectedLocationId === null}
          onClick={() => onSelectLocation(createNewLocation())}
          sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}
        >
          Create New Location
        </Button>
        
        {/* Export/Import/Print buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Tooltip title="Sort Locations">
            <span>
              <Button
                size="small"
                startIcon={<SortIcon />}
                onClick={handleSortClick}
                disabled={loading || savedLocations.length === 0}
              >
                Sort
              </Button>
            </span>
          </Tooltip>
          
          <Tooltip title="Export Locations">
            <span>
              <Button
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={loading || savedLocations.length === 0}
              >
                Export
              </Button>
            </span>
          </Tooltip>
          
          <Tooltip title="Import Locations">
            <span>
              <Button
                size="small"
                startIcon={<FileUploadIcon />}
                onClick={handleImport}
                disabled={loading}
              >
                Import
              </Button>
            </span>
          </Tooltip>
          
          <Tooltip title="Print Report">
            <span>
              <Button
                size="small"
                startIcon={<PrintIcon />}
                onClick={() => setPrintReportOpen(true)}
                disabled={loading || savedLocations.length === 0}
              >
                Print
              </Button>
            </span>
          </Tooltip>
        </Box>
        
        {/* Delete All button in separate row */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title="Delete All Locations">
            <span>
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteAllClick}
                disabled={loading || savedLocations.length === 0}
                color="error"
                variant="outlined"
              >
                Delete All
              </Button>
            </span>
          </Tooltip>
        </Box>
        
        {/* Search and filter */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search locations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.875rem'
              }
            }}
          />
        </Box>
      </Box>
      
      {/* Error and success messages */}
      {routeError && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2, mx: 1 }}
          onClose={() => setRouteError(null)}
        >
          {routeError}
        </Alert>
      )}
      
      {/* Sort menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
      >
        <MenuItem onClick={() => handleSortChange('order')}>
          <ListItemIcon>
            <DragHandleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Route Order</ListItemText>
          {sortBy === 'order' && (
            <ListItemIcon sx={{ ml: 1 }}>
              {sortDirection === 'asc' ? '↑' : '↓'}
            </ListItemIcon>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('name')}>
          <ListItemIcon>
            <span style={{ fontSize: '1.2rem' }}>A</span>
          </ListItemIcon>
          <ListItemText>Name</ListItemText>
          {sortBy === 'name' && (
            <ListItemIcon sx={{ ml: 1 }}>
              {sortDirection === 'asc' ? '↑' : '↓'}
            </ListItemIcon>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('date')}>
          <ListItemIcon>
            <CalendarTodayIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Date Added</ListItemText>
          {sortBy === 'date' && (
            <ListItemIcon sx={{ ml: 1 }}>
              {sortDirection === 'asc' ? '↑' : '↓'}
            </ListItemIcon>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleSortChange('direction')}>
          <ListItemIcon>
            <StraightenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Row Direction</ListItemText>
          {sortBy === 'direction' && (
            <ListItemIcon sx={{ ml: 1 }}>
              {sortDirection === 'asc' ? '↑' : '↓'}
            </ListItemIcon>
          )}
        </MenuItem>
      </Menu>
      
      {/* Location list */}
      <Box sx={{ 
        flexGrow: 1, 
        overflowY: 'auto',
        px: 1,
        mr: -0.5,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#CBD5E1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#94A3B8',
        }
      }}>
        {loading ? (
          // Loading skeletons
          Array.from(new Array(3)).map((_, index) => (
            <Card key={index} sx={{ mb: 0.75, borderRadius: 1.5 }}>
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Skeleton animation="wave" height={16} width="80%" />
                <Skeleton animation="wave" height={12} width="40%" />
                <Skeleton animation="wave" height={12} width="60%" />
              </CardContent>
            </Card>
          ))
        ) : filteredAndSortedLocations.length === 0 ? (
          // Empty state
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            p: 2,
            textAlign: 'center'
          }}>
            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
              {searchTerm ? 'No locations match your search' : 'No saved locations yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {searchTerm 
                ? 'Try a different search term' 
                : 'Use the save button in the shadow projection panel to save locations'}
            </Typography>
          </Box>
        ) : (
          // Location cards with drag and drop
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="saved-locations-list">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {filteredAndSortedLocations.map((location, index) => (
                    <Draggable key={location.id.toString()} draggableId={location.id.toString()} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.8 : 1,
                            marginBottom: '8px',
                          }}
                        >
                          <Card 
                            sx={{ 
                              mb: 0, 
                              cursor: snapshot.isDragging ? 'grabbing' : 'pointer',
                              border: selectedLocationId === location.id ? '2px solid #1976d2' : 'none',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                transform: snapshot.isDragging ? 'none' : 'translateY(-2px)',
                                boxShadow: 3
                              },
                              borderRadius: 1.5,
                              width: '100%'
                            }}
                            onClick={snapshot.isDragging ? undefined : () => {
                              if (location.id === "-1") {
                                onSelectLocation(createNewLocation());
                              } else {
                                onSelectLocation(location);
                              }
                            }}
                          >
                            <CardContent sx={{ p: 0.75, '&:last-child': { pb: 0.75 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    {...provided.dragHandleProps} 
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ 
                                      display: 'flex',
                                      cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                      mr: 1,
                                      p: 0.5,
                                      borderRadius: '4px',
                                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                      '&:hover': {
                                        bgcolor: 'rgba(0, 0, 0, 0.09)'
                                      }
                                    }}
                                  >
                                    <DragHandleIcon sx={{ fontSize: '0.9rem', color: snapshot.isDragging ? 'primary.main' : 'text.secondary' }} />
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                                      {location.order !== undefined ? `${Number(location.order) + 1}. ` : ''}{location.name}
                                    </Typography>
                                    {/* Route information next to name */}
                                    {index === 0 && routeData[`current_to_${location.id}`] && (
                                      <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        sx={{ 
                                          fontSize: '0.7rem', 
                                          ml: 1
                                        }}
                                      >
                                        From current {routeData[`current_to_${location.id}`]?.time} ({routeData[`current_to_${location.id}`]?.distance})
                                      </Typography>
                                    )}
                                    {index > 0 && routeData[location.id.toString()] && (
                                      <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        sx={{ 
                                          fontSize: '0.7rem', 
                                          ml: 1
                                        }}
                                      >
                                        {routeData[location.id.toString()]?.time} ({routeData[location.id.toString()]?.distance})
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Tooltip title="Navigate in Google Maps">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(getGoogleMapsLink(location.latitude, location.longitude), '_blank');
                                      }}
                                      aria-label="navigate to location"
                                      sx={{ padding: 0.25, mr: 0.5 }}
                                    >
                                      <DirectionsCarIcon sx={{ fontSize: '0.8rem' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(location.id.toString(), e);
                                    }}
                                    aria-label="delete location"
                                    sx={{ padding: 0.25 }}
                                  >
                                    <DeleteIcon sx={{ fontSize: '0.8rem' }} />
                                  </IconButton>
                                </Box>
                              </Box>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <CalendarTodayIcon fontSize="small" sx={{ mr: 0.25, fontSize: '0.7rem', color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {format(parseISO(location.selected_date), 'MMM d, yyyy')}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <StraightenIcon fontSize="small" sx={{ mr: 0.25, fontSize: '0.7rem', color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {location.row_direction}° ({formatCardinalDirectionPair(location.cardinal_direction || getCardinalDirection(location.row_direction))})
                                  </Typography>
                                </Box>
                              </Box>
                              
                              <Box sx={{ 
                                mt: 0.5, // Adjusted margin top slightly
                                backgroundColor: 'rgba(56, 161, 105, 0.08)', 
                                borderRadius: 1,
                                p: 0.5
                              }}>
                                {typeof formatTimeWindows(location.optimal_time_windows) === 'string' 
                                  ? <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                      {formatTimeWindows(location.optimal_time_windows)}
                                    </Typography>
                                  : (formatTimeWindows(location.optimal_time_windows) as React.ReactElement[]).map((element: React.ReactElement, index: number) => 
                                    React.cloneElement(element, { 
                                      key: index,
                                      sx: { 
                                        ...element.props.sx,
                                        mb: index < (formatTimeWindows(location.optimal_time_windows) as React.ReactElement[]).length - 1 ? 0.25 : 0,
                                      }
                                    })
                                  )
                                }
                              </Box>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Box>
      
      <ConfirmationDialog
        open={confirmDeleteOpen}
        title="Delete Location"
        content="Are you sure you want to delete this saved location? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
      
      {/* Delete All Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDeleteAllOpen}
        title="Delete All Locations"
        content="Are you sure you want to delete ALL saved locations? This action cannot be undone."
        onConfirm={handleConfirmDeleteAll}
        onCancel={() => setConfirmDeleteAllOpen(false)}
      />

      {/* Print Report Dialog */}
      <PrintableReport
        open={printReportOpen}
        onClose={() => setPrintReportOpen(false)}
        savedLocations={savedLocations}
      />
    </Paper>
  );
};

export default SavedLocationsPanel; 